import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPrintDocument } from '@/proposals/lib/api';
import { errorMessage } from '@/lib/api';
import { DocumentView } from '@/proposals/document/DocumentView';
import { printDocumentTitle } from '@/proposals/lib/print-title';

/**
 * The print view of a proposal: the resolved document rendered full-bleed and
 * unscoped (no editor chrome), the byte-identical paper the editor shows. It
 * is mounted outside the admin shell and Clerk. Two callers:
 *
 * - a person, whose same-origin session cookie authenticates the fetch and who
 *   gets the browser's print / Save-as-PDF dialog once fonts have settled;
 * - the PDF render service (headless Chromium), which arrives with
 *   `?headless=1` and a scoped print token in the URL fragment, gets no dialog,
 *   and waits for `data-print-ready` on <body> before printing.
 *
 * @returns The printable proposal.
 */
export function PrintPage(): React.ReactElement {
  const id = window.location.pathname.split('/')[2] ?? '';
  const headless = new URLSearchParams(window.location.search).get('headless') === '1';
  const doc = useQuery({
    queryKey: ['proposal-print', id],
    queryFn: () => getPrintDocument(id),
    enabled: id.length > 0,
  });

  // DocumentView (print) measures fonts + paginates over-tall pages, then calls
  // onReady. Only once pagination has settled do we signal print-ready / open the
  // dialog — printing earlier would capture the pre-paginated (clipped) layout.
  const [paginated, setPaginated] = useState(false);
  const onReady = useCallback(() => setPaginated(true), []);

  // The tab's title becomes the PDF's title and its suggested file name, so it
  // names the proposal. Set as soon as the document loads, well before print-ready.
  const title = doc.data ? printDocumentTitle(doc.data) : null;
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  useEffect(() => {
    if (doc.isError) {
      document.body.dataset['printError'] = '1';
      return;
    }
    if (!doc.data || !paginated) return;
    let cancelled = false;
    // A final frame so the paginated sheets are painted before we print.
    const id = window.setTimeout(() => {
      if (cancelled) return;
      document.body.dataset['printReady'] = '1';
      if (!headless) window.print();
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [doc.data, doc.isError, headless, paginated]);

  const message = (text: string): React.ReactElement => (
    <p style={{ padding: 24, font: '14px -apple-system, system-ui, sans-serif', color: '#475569' }}>
      {text}
    </p>
  );

  if (doc.isPending) return message('Preparing the proposal…');
  // A proposal that is not ready (template sample text, an empty required
  // field) is refused by the server; say why rather than "could not load".
  if (doc.isError) return message(errorMessage(doc.error));
  if (!doc.data) return message('Could not load this proposal.');
  return (
    <>
      {/* Force the browser to print the branded backgrounds (navy cover, orbs,
          coloured table headers) even when "Background graphics" is unchecked
          in the print dialog. The server-side PDF sets printBackground too. */}
      <style>{`html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
@media print{*,*::before,*::after{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}}`}</style>
      <DocumentView doc={doc.data} print onReady={onReady} />
    </>
  );
}
